import type { VariantProps } from "class-variance-authority";
import { Trash } from "lucide-react";
import { useDispatch } from "react-redux";

import { useBulkFileOperations } from "@/hooks/mutations/use-file-utils";
import { setMessage } from "@/redux/notification";

import DeleteIcon from "@assets/images/file-with-trash.png";

import { ConfirmationModal } from "../../ConfirmationModal";
import { Button, buttonVariants } from "../../ui/button";

type ButtonSize = VariantProps<typeof buttonVariants>["size"];

type DeleteMultipleFilesConfirmationProps = {
  fileIds: string[];
  onDeleteSuccess?: () => void;
  buttonSize?: ButtonSize;
};

export const DeleteMultipleFilesConfirmation = ({
  fileIds,
  onDeleteSuccess,
  buttonSize = "sm",
}: DeleteMultipleFilesConfirmationProps) => {
  const dispatch = useDispatch();
  const { bulkRemove } = useBulkFileOperations();

  const handleDelete = () => {
    bulkRemove.mutate(fileIds, {
      onSuccess: (result) => {
        const { successCount, errorCount, errors } = result;

        if (errorCount === 0) {
          dispatch(
            setMessage({
              content: `${successCount} files deleted successfully`,
              type: "success",
            })
          );
        } else if (successCount > 0) {
          dispatch(
            setMessage({
              content: `${successCount} files deleted successfully, ${errorCount} failed`,
              type: "warning",
            })
          );
        } else {
          dispatch(
            setMessage({
              content: `Failed to delete files: ${errors.map((e) => e.error).join(", ")}`,
              type: "error",
            })
          );
        }

        // Call the callback to clear selection if any files were deleted
        if (successCount > 0) {
          onDeleteSuccess?.();
        }
      },
      onError: (error) => {
        dispatch(
          setMessage({
            content: error.message || "Failed to delete files",
            type: "error",
          })
        );
      },
    });
  };

  return (
    <ConfirmationModal
      type="warning"
      icon={
        <img
          src={DeleteIcon}
          alt="Delete Icon"
          className="size-14 text-destructive"
        />
      }
      title="Delete Selected Files?"
      description={`This action will permanently delete ${fileIds.length} selected file${fileIds.length === 1 ? "" : "s"} and cannot be undone. Please confirm to proceed.`}
      confirmButtonText="Yes, Delete"
      cancelButtonText="No, Cancel"
      onConfirm={handleDelete}
      isLoading={bulkRemove.isPending}
    >
      <Button
        variant="destructive-outline"
        size={buttonSize}
        disabled={bulkRemove.isPending}
      >
        <Trash className="text-destructive" />
        Delete
      </Button>
    </ConfirmationModal>
  );
};
